import { useState, useEffect, useMemo, useCallback } from "react";
import { useKeywordPostsQuery } from "@/features/keyword-monitoring/api/keyword.queries";
import { useKeywordStore } from "@/features/keyword-monitoring/store/keywordStore";
import { useKeywordsStore } from "@/stores/keywordsStore";
import { useKeywordApiToken } from "@/features/auth/hooks/useApiToken";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { MONITORING_TYPES } from "@/constants/monitoring";
import { generateKeywordEndpoint } from "@/lib/endpoint";
import { filterPostFields } from "@/features/keyword-monitoring/utils";
import { useContainerLaunch } from "@/hooks/useContainerLaunch";
import { useContainerData } from "@/hooks/useContainerData";
import { useTokenManagement } from "@/hooks/useTokenManagement";

/**
 * Controller hook for KeywordContainer
 * Manages all business logic, state, and side effects
 */
export const useKeywordContainer = () => {
  const state = useKeywordStore();
  const keywords = useKeywordsStore((s) => s.keywords);
  const { apiToken, isPending: isApiTokenLoading } = useKeywordApiToken();
  const { user } = useAuthStore();

  const [selectedPlatformFilter, setSelectedPlatformFilter] =
    useState<string>("all");

  // Launch management
  const {
    showResults,
    hasLaunched,
    handleShowResults,
    handleLaunch: baseLaunch,
  } = useContainerLaunch();

  // Data management
  const {
    data: postsData,
    isLoading,
    refetch,
  } = useKeywordPostsQuery(state, hasLaunched);

  const { handleCopyCode } = useContainerData(postsData);

  // Token management
  const shouldShowEndpoint = useMemo(
    () =>
      keywords.main.length > 0 ||
      keywords.additional.length > 0 ||
      keywords.excluded.length > 0 ||
      state.selectedSubFields.length > 0 ||
      state.selectedPlatforms.length > 0,
    [keywords, state.selectedSubFields, state.selectedPlatforms],
  );

  const {
    selectedToken,
    effectiveToken,
    isLaunchDisabled,
    handleTokenSelect,
    trackUsage,
    availableTokens,
    tokensLoading,
  } = useTokenManagement({
    apiToken,
    isApiTokenLoading,
    user,
    showTokenDropdown: true,
    additionalValidation: () => shouldShowEndpoint,
    monitoringType: "keyword",
  });

  // Use selected token's platforms so disabled state matches the token in the dropdown
  const tokenSelectedPlatforms = selectedToken?.selected_platforms ?? [];

  // API endpoint generation
  const apiEndpoint = useMemo(
    () =>
      generateKeywordEndpoint({
        keywords: keywords,
        selectedPlatforms: state.selectedPlatforms,
        dateRange: state.dateRange,
        selectedSubFields: state.selectedSubFields,
        limit: state.limit,
      }) || "",
    [
      keywords,
      state.selectedPlatforms,
      state.dateRange,
      state.selectedSubFields,
      state.limit,
    ],
  );

  // Data filtering
  const filteredData = useMemo(() => {
    if (!postsData?.data) return postsData;

    if (selectedPlatformFilter === "all") {
      const allPosts: any[] = [];
      Object.values(postsData.data).forEach((platformPosts) => {
        if (Array.isArray(platformPosts)) {
          allPosts.push(...platformPosts);
        }
      });

      return {
        ...postsData,
        data: allPosts.map((post: any) =>
          filterPostFields(post, state.selectedSubFields),
        ),
      };
    }

    const platformData = postsData.data[selectedPlatformFilter] || [];
    return {
      ...postsData,
      data: platformData.map((post: any) =>
        filterPostFields(post, state.selectedSubFields),
      ),
    };
  }, [postsData, selectedPlatformFilter, state.selectedSubFields]);

  // Results count
  const resultsCount = useMemo(() => {
    if (!postsData?.data) return 0;

    if (selectedPlatformFilter === "all") {
      return Object.values(postsData.data).reduce(
        (total: number, platformData) =>
          total + (Array.isArray(platformData) ? platformData.length : 0),
        0,
      );
    }

    const platformData = postsData.data[selectedPlatformFilter] || [];
    return Array.isArray(platformData) ? platformData.length : 0;
  }, [postsData?.data, selectedPlatformFilter]);

  // Empty results: API returned success but no data (e.g. data: [], metadata.total: 0)
  const isEmptyResults = useMemo(() => {
    if (!postsData) return false;
    const d = postsData.data;
    const meta = postsData.metadata;
    if (Array.isArray(d) && d.length === 0) return true;
    if (meta?.total === 0) return true;
    if (d && typeof d === "object" && !Array.isArray(d)) {
      const hasAny = Object.values(d).some(
        (v: unknown) => Array.isArray(v) && v.length > 0,
      );
      return !hasAny;
    }
    return false;
  }, [postsData]);

  // Track token usage
  useEffect(() => {
    if (postsData && !isLoading) {
      const count = postsData?.data
        ? Object.values(postsData.data).flat().length
        : 0;
      trackUsage(count);
    }
  }, [postsData, isLoading, trackUsage]);

  // Launch handler
  const handleLaunch = useCallback(() => {
    handleShowResults(true);
    baseLaunch(refetch);
  }, [handleShowResults, baseLaunch, refetch]);

  return {
    // State
    state,
    postsData,
    filteredData,
    isLoading,
    showResults,
    hasLaunched,
    selectedPlatformFilter,
    resultsCount,
    isEmptyResults,
    apiEndpoint: shouldShowEndpoint ? apiEndpoint : "",
    responseJSON: JSON.stringify(filteredData, null, 2),

    // Token
    selectedToken,
    effectiveToken,
    isLaunchDisabled,
    apiToken,
    isApiTokenLoading,
    tokenSelectedPlatforms,
    availableTokens,
    tokensLoading,

    // Handlers
    handleShowResults,
    handleLaunch,
    handleCopyCode,
    handleTokenSelect,
    setSelectedPlatformFilter,

    // Constants
    monitoringType: MONITORING_TYPES.KEYWORD,
  };
};
